/*
 * Frank's Body Buffet landing page.
 *
 * One small controller for everything on the page that talks to the cart:
 *   - kit controls (add / stepper) in the menu and the set menus
 *   - the order tray (progress to the free gift + checkout)
 *   - the "A treat on me" receipt
 *
 * It does NOT add the free gift. That is done by the theme's gift app
 * (cart-gift.js + the gift discount function). This page only displays
 * progress, and it uses the SAME rule the gift app uses:
 *   - threshold: the enabled "threshold" rule in the custom.gift_config shop
 *     metafield for the gift product (rendered by Liquid into the
 *     [data-buffet-config] element), falling back to the section's preview
 *     threshold only while no rule has been set up yet;
 *   - subtotal: the sum of every non-gift line's final_line_price, exactly as
 *     cart-gift.js computes it (after line-level discounts, before shipping).
 *
 * Cart changes are published on the theme's own cartUpdate channel, so the
 * cart drawer refreshes and the gift app re-evaluates without any extra work.
 */
(function () {
  "use strict";

  // Several sections load this file; run once, and work across all of them.
  if (window.__fbBuffetLoaded) return;
  window.__fbBuffetLoaded = true;
  if (!document.querySelector("[data-buffet], [data-buffet-hero]")) return;
  var root = document;
  var host = document.body;

  var config = {};
  try {
    config = JSON.parse(
      (document.querySelector("[data-buffet-config]") || {}).textContent || "{}",
    );
  } catch (e) {
    config = {};
  }

  var cart = null;
  var busy = false;

  var currency =
    (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
    config.currency ||
    "AUD";
  var locale = document.documentElement.lang || "en-AU";

  function money(cents) {
    var value = cents / 100;
    var whole = Math.abs(value - Math.round(value)) < 0.005;
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currency,
        currencyDisplay: "narrowSymbol",
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2,
      }).format(value);
    } catch (e) {
      return "$" + (whole ? Math.round(value) : value.toFixed(2));
    }
  }

  function isGift(item) {
    var p = item.properties || {};
    return p._gift === "true" || p._gift === true;
  }

  // On the published theme, never promise a gift the gift app has not been
  // told to give: with no matching rule, gift progress is hidden. Unpublished
  // previews keep the section's preview threshold so the page can be tested.
  var liveTheme = !!(window.Shopify && window.Shopify.theme && window.Shopify.theme.role === "main");
  var giftLive = String(config.ruleFound) === "true" || !liveTheme;

  // The offer's own switch and end time (Theme settings → Holiday buffet),
  // checked again here because a cached page can outlive the end time.
  // Everything marked data-buffet-gift-only goes once it is over, and the
  // gift progress with it (giftLive feeds the threshold).
  var giftEndsMs = Number(config.giftEnds) > 0 ? Number(config.giftEnds) * 1000 : 0;
  var giftActive = config.giftOn !== false && (!giftEndsMs || Date.now() < giftEndsMs);
  giftLive = giftLive && giftActive;
  function hideGiftOnly() {
    document.querySelectorAll("[data-buffet-gift-only], [data-buffet-gift-promise]").forEach(function (el) {
      el.hidden = true;
    });
  }
  if (!giftActive) hideGiftOnly();
  else if (giftEndsMs && giftEndsMs - Date.now() < 864e5) {
    // Open across the deadline: drop the offer at the minute it ends.
    setTimeout(function () {
      giftLive = false;
      hideGiftOnly();
      refresh();
    }, giftEndsMs - Date.now());
  }

  // The hero ticket and strip quote the threshold; they stay hidden unless
  // the gift is actually going to be given (see giftLive above).
  if (giftLive) {
    document.querySelectorAll("[data-buffet-gift-promise]").forEach(function (el) {
      el.hidden = false;
    });
  }

  function threshold() {
    if (!giftLive) return 0;
    return Number(config.threshold) > 0 ? Number(config.threshold) : 0;
  }

  // Same figure cart-gift.js uses to decide whether a threshold rule matches.
  function subtotal(c) {
    return (
      (c.items || []).reduce(function (sum, item) {
        return isGift(item) ? sum : sum + (item.final_line_price || 0);
      }, 0) / 100
    );
  }

  function qtyForVariant(c, variantId) {
    var n = 0;
    (c.items || []).forEach(function (item) {
      if (!isGift(item) && String(item.variant_id) === String(variantId)) n += item.quantity;
    });
    return n;
  }

  function lineKeyForVariant(c, variantId) {
    var found = null;
    (c.items || []).forEach(function (item) {
      if (!found && !isGift(item) && String(item.variant_id) === String(variantId)) found = item;
    });
    return found;
  }

  function fetchCart() {
    return fetch((window.routes && window.routes.cart_url ? window.routes.cart_url : "/cart") + ".js", {
      headers: { Accept: "application/json" },
    }).then(function (r) {
      return r.json();
    });
  }

  function announce(text) {
    var live = root.querySelector("[data-buffet-live]");
    if (live) live.textContent = text;
  }

  function notifyCartChanged() {
    // Re-render the drawer and the header cart icon without opening the drawer
    // (the theme listens for this event), then tell the gift app and anything
    // else on the cartUpdate channel.
    document.dispatchEvent(new CustomEvent("cart-drawer:refresh"));
    if (typeof publish === "function" && window.PUB_SUB_EVENTS) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: "buffet" });
    }
  }

  // While an add or a quantity change is on its way (the add, then the cart
  // re-read, can take a second or two) the item shows it: the Add button gets
  // the theme's own loading ring (as the product page's Add to cart does) and
  // a stepper dims its count. Cleared once the cart has been re-read.
  function setBusy(kit, on) {
    if (!kit) return;
    kit.classList.toggle("is-busy", on);
    var add = kit.querySelector("[data-buffet-add]");
    if (add) {
      add.classList.toggle("loading", on);
      add.setAttribute("aria-disabled", on ? "true" : "false");
      var spin = add.querySelector(".loading-overlay__spinner");
      if (spin) spin.classList.toggle("hidden", !on);
    }
  }

  function addVariant(variantId, name, kit) {
    if (busy) return Promise.resolve();
    busy = true;
    setBusy(kit, true);
    host.setAttribute("aria-busy", "true");
    var url = (window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : "/cart/add") + ".js";
    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
    })
      .then(function (r) {
        return r.json().then(function (body) {
          if (!r.ok) throw new Error(body.description || body.message || "Could not add");
          return body;
        });
      })
      .then(function () {
        announce((name || "Item") + " added to your order");
        notifyCartChanged();
      })
      .catch(function (err) {
        announce(err.message || "Sorry, that could not be added");
      })
      .then(function () {
        return refresh();
      })
      .then(function () {
        busy = false;
        setBusy(kit, false);
        host.removeAttribute("aria-busy");
      });
  }

  function setQuantity(variantId, qty, name, kit) {
    if (busy) return Promise.resolve();
    var line = lineKeyForVariant(cart || { items: [] }, variantId);
    if (!line) return Promise.resolve();
    busy = true;
    setBusy(kit, true);
    host.setAttribute("aria-busy", "true");
    var url = (window.routes && window.routes.cart_change_url ? window.routes.cart_change_url : "/cart/change") + ".js";
    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: line.key, quantity: qty }),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function () {
        announce((name || "Item") + (qty === 0 ? " removed from your order" : " quantity updated"));
        notifyCartChanged();
      })
      .catch(function () {})
      .then(function () {
        return refresh();
      })
      .then(function () {
        busy = false;
        setBusy(kit, false);
        host.removeAttribute("aria-busy");
      });
  }

  // ---- rendering -----------------------------------------------------------

  function renderKits(c) {
    root.querySelectorAll("[data-buffet-kit]").forEach(function (el) {
      var id = el.getAttribute("data-variant-id");
      if (!id) return;
      var qty = qtyForVariant(c, id);
      var add = el.querySelector("[data-buffet-add]");
      var stepper = el.querySelector("[data-buffet-stepper]");
      var count = el.querySelector("[data-buffet-count]");
      var status = el.querySelector("[data-buffet-status]");
      var wasIn = el.classList.contains("is-in-order");
      el.classList.toggle("is-in-order", qty > 0);
      // Just added: the stepper / "In your order" box fades in, so the
      // change from the button is seen rather than a jump.
      if (qty > 0 && !wasIn && el.hasAttribute("data-buffet-rendered")) {
        el.classList.remove("is-new");
        void el.offsetWidth;
        el.classList.add("is-new");
      }
      el.setAttribute("data-buffet-rendered", "");
      if (stepper) {
        stepper.hidden = qty === 0;
        if (add) add.hidden = qty > 0;
        if (count) count.textContent = qty;
      } else if (add) {
        // Set-menu cards: once the kit is in the order the button gives way
        // to an "In your order (n)" box, as in the mockup.
        add.hidden = qty > 0;
      }
      if (status) {
        status.hidden = qty === 0;
        status.textContent = qty > 0 ? status.getAttribute("data-template").replace("{n}", qty) : "";
      }
    });
  }

  function progress(c) {
    var t = threshold();
    var sub = subtotal(c);
    var has = (c.items || []).some(function (i) {
      return !isGift(i);
    });
    if (!t) return { has: has, sub: sub, t: 0, pct: 0, unlocked: false, remaining: 0 };
    var unlocked = sub >= t;
    return {
      has: has,
      sub: sub,
      t: t,
      pct: Math.max(0, Math.min(100, Math.round((sub / t) * 100))),
      unlocked: unlocked,
      remaining: Math.max(0, t - sub),
    };
  }

  function progressMessage(p) {
    var name = config.giftName || "free gift";
    if (!p.t) return "";
    if (p.unlocked) return "Treat unlocked. Your " + name + " is on the house.";
    if (!p.has) return "Spend " + money(p.t * 100) + " and your " + name + " is on the house.";
    return "Add " + money(p.remaining * 100) + " more and your " + name + " is on the house.";
  }

  function renderTray(c) {
    var tray = root.querySelector("[data-buffet-tray]");
    if (!tray) return;
    var p = progress(c);
    var count = (c.items || []).reduce(function (n, i) {
      return isGift(i) ? n : n + i.quantity;
    }, 0);
    tray.hidden = !p.has;
    var label = tray.querySelector("[data-buffet-tray-count]");
    if (label) label.textContent = count + (count === 1 ? " item" : " items");
    var msg = tray.querySelector("[data-buffet-tray-message]");
    if (msg) msg.textContent = progressMessage(p);
    var trayMeter = tray.querySelector(".fb-bar");
    if (trayMeter) trayMeter.hidden = !p.t;
    var bar = tray.querySelector("[data-buffet-tray-bar]");
    if (bar) bar.style.width = p.pct + "%";
    var meter = tray.querySelector("[role=progressbar]");
    if (meter) meter.setAttribute("aria-valuenow", p.pct);
    tray.classList.toggle("is-unlocked", p.unlocked);
  }

  function renderReceipt(c) {
    root.querySelectorAll("[data-buffet-receipt]").forEach(function (el) {
      var p = progress(c);
      var lines = el.querySelector("[data-receipt-lines]");
      var emptyRow = el.querySelector("[data-receipt-empty]");
      var gift = el.querySelector("[data-receipt-gift]");
      var giftPending = el.querySelector("[data-receipt-gift-pending]");
      var msg = el.querySelector("[data-receipt-message]");
      var bar = el.querySelector("[data-receipt-bar]");
      var items = (c.items || []).filter(function (i) {
        return !isGift(i);
      });

      if (lines) {
        lines.innerHTML = "";
        var shown = items.slice(0, 5);
        shown.forEach(function (item) {
          var row = document.createElement("div");
          row.className = "fb-receipt__row";
          var l = document.createElement("span");
          l.textContent = (item.quantity > 1 ? item.quantity + " × " : "") + item.product_title;
          var dots = document.createElement("span");
          dots.className = "fb-receipt__dots";
          dots.setAttribute("aria-hidden", "true");
          var price = document.createElement("span");
          price.textContent = money(item.final_line_price);
          row.appendChild(l);
          row.appendChild(dots);
          row.appendChild(price);
          lines.appendChild(row);
        });
        if (items.length > shown.length) {
          var more = document.createElement("div");
          more.className = "fb-receipt__more";
          more.textContent = "+ " + (items.length - shown.length) + " more in your order";
          lines.appendChild(more);
        }
      }
      if (emptyRow) emptyRow.hidden = items.length > 0;
      var total = el.querySelector("[data-receipt-total]");
      if (total) {
        total.hidden = items.length === 0;
        var totalValue = total.querySelector("[data-receipt-total-value]");
        if (totalValue) totalValue.textContent = money(c.total_price || 0);
      }
      var prog = el.querySelector(".fb-receipt__progress");
      if (prog) prog.hidden = !p.t;
      if (gift) gift.hidden = !p.unlocked;
      if (giftPending) giftPending.hidden = p.unlocked;
      if (msg) msg.textContent = progressMessage(p);
      if (bar) bar.style.width = p.pct + "%";
      var meter = el.querySelector("[role=progressbar]");
      if (meter) meter.setAttribute("aria-valuenow", p.pct);
      el.classList.toggle("is-unlocked", p.unlocked);
      placeStamp(el);
    });
  }

  // The "On the house" stamp belongs to the free gift, so it is centred on
  // the gift's line rather than parked at a fixed height, where it landed on
  // whatever product happened to be first in the cart.
  function placeStamp(el) {
    var wrap = el.closest(".fb-receipt-wrap") || el;
    var stamp = wrap.querySelector(".fb-receipt__stamp");
    var gift = el.querySelector("[data-receipt-gift]");
    if (!stamp || !gift || gift.hidden) return;
    // Layout offsets, not on-screen boxes: the paper slides in with its
    // "printing" animation, and a box measured mid-slide put the stamp above
    // the receipt. offsetTop ignores transforms.
    var top = 0;
    var node = gift;
    while (node && node !== wrap) {
      top += node.offsetTop;
      node = node.offsetParent;
    }
    if (node !== wrap) top = gift.getBoundingClientRect().top - wrap.getBoundingClientRect().top;
    var y = top + gift.offsetHeight / 2 - stamp.offsetHeight / 2;
    wrap.style.setProperty("--fb-stamp-top", Math.round(y) + "px");
  }
  window.addEventListener("resize", function () {
    root.querySelectorAll("[data-buffet-receipt]").forEach(placeStamp);
  });

  function render(c) {
    cart = c;
    renderKits(c);
    renderTray(c);
    renderReceipt(c);
  }

  var refreshTimer = null;
  function refresh() {
    return fetchCart().then(render).catch(function () {});
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, 250);
  }

  // ---- events --------------------------------------------------------------

  root.addEventListener("click", function (event) {
    var kit = event.target.closest("[data-buffet-kit]");
    if (!kit) return;
    var id = kit.getAttribute("data-variant-id");
    var name = kit.getAttribute("data-name");
    if (event.target.closest("[data-buffet-add]")) {
      event.preventDefault();
      addVariant(id, name, kit);
    } else if (event.target.closest("[data-buffet-plus]")) {
      event.preventDefault();
      addVariant(id, name, kit);
    } else if (event.target.closest("[data-buffet-minus]")) {
      event.preventDefault();
      setQuantity(id, Math.max(0, qtyForVariant(cart || { items: [] }, id) - 1), name, kit);
    }
  });

  root.querySelectorAll("[data-buffet-checkout]").forEach(function (a) {
    a.addEventListener("click", function (event) {
      event.preventDefault();
      window.location.href = "/checkout";
    });
  });

  // Keep in step with everything else that changes the cart (drawer, gift app).
  if (typeof subscribe === "function" && window.PUB_SUB_EVENTS) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (payload) {
      if (payload && payload.source === "buffet") return;
      scheduleRefresh();
    });
  }
  window.addEventListener("pageshow", scheduleRefresh);

  // ---- sticky course buttons (mobile) --------------------------------------

  function setStickyOffset() {
    var header = document.querySelector(".page-header-sticky .header-wrapper, #shopify-section-header, header");
    var body = document.body;
    var top = 0;
    if (header && body.className.indexOf("page-header-sticky") !== -1) {
      top = Math.round(header.getBoundingClientRect().height);
    }
    document.documentElement.style.setProperty("--fb-sticky-top", top + "px");
  }
  setStickyOffset();
  window.addEventListener("resize", setStickyOffset);

  // ---- animated jumps within the page ---------------------------------------
  // "See the menu", the phone course chips and "Get me there" glide to their
  // target instead of jumping, stopping clear of the sticky header (and of the
  // sticky chips when the target is a course). Instant for anyone who has
  // asked their device for reduced motion.
  var reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)");
  function headerOffset(target) {
    var header = document.querySelector(".page-header-sticky .header-wrapper, #shopify-section-header, header");
    var top = 0;
    // The header's own height, not where it sits now: at the top of the page
    // the announcement bar is above it, but it scrolls away on the way down.
    if (header && document.body.className.indexOf("page-header-sticky") !== -1) {
      top = header.getBoundingClientRect().height;
    }
    if (target.classList.contains("fb-course")) {
      var chips = document.querySelector("[data-buffet-courses]");
      if (chips && getComputedStyle(chips).display !== "none") top += chips.getBoundingClientRect().height;
      top += 12;
    }
    return top;
  }
  document.addEventListener("click", function (event) {
    var link = event.target.closest(".fb-buffet a[href^='#']");
    if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;
    var id = decodeURIComponent(link.getAttribute("href").slice(1));
    var target = id && document.getElementById(id);
    if (!target) return;
    event.preventDefault();
    var y = target.getBoundingClientRect().top + window.pageYOffset - headerOffset(target);
    window.scrollTo({ top: Math.max(0, y), behavior: reduceMotion && reduceMotion.matches ? "auto" : "smooth" });
    if (history.replaceState) history.replaceState(null, "", "#" + id);
    // Keyboard and screen-reader users land where they asked to go.
    if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
    target.focus({ preventScroll: true });
  });

  var nav = root.querySelector("[data-buffet-courses]");
  if (nav && "IntersectionObserver" in window) {
    var links = nav.querySelectorAll("a[href^='#']");
    var map = {};
    links.forEach(function (a) {
      map[a.getAttribute("href").slice(1)] = a;
    });
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting && map[e.target.id]) {
            links.forEach(function (a) {
              a.removeAttribute("aria-current");
            });
            map[e.target.id].setAttribute("aria-current", "true");
          }
        });
      },
      { rootMargin: "-35% 0px -55% 0px" },
    );
    Object.keys(map).forEach(function (id) {
      var target = document.getElementById(id);
      if (target) io.observe(target);
    });
  }

  // ---- hero video pause ----------------------------------------------------

  document.querySelectorAll("[data-buffet-hero]").forEach(function (hero) {
    var video = hero.querySelector("video");
    var button = hero.querySelector("[data-buffet-pause]");
    if (!video || !button) return;
    var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    function sync() {
      var paused = video.paused;
      button.setAttribute("aria-pressed", paused ? "true" : "false");
      button.setAttribute("aria-label", paused ? button.getAttribute("data-label-play") : button.getAttribute("data-label-pause"));
      button.classList.toggle("is-paused", paused);
    }
    if (reduce) video.pause();
    sync();
    button.addEventListener("click", function () {
      if (video.paused) video.play().catch(function () {});
      else video.pause();
      sync();
    });
    video.addEventListener("play", sync);
    video.addEventListener("pause", sync);
  });

  // ---- hero fills the first screen ----------------------------------------
  // The hero's height is "screen minus whatever sits above it" (announcement
  // bar + header, which differ per store); CSS reads it from --fb-hero-top.
  var heroStage = document.querySelector(".fb-hero__stage");
  if (heroStage) {
    var setHeroTop = function () {
      var top = heroStage.getBoundingClientRect().top + window.pageYOffset;
      document.documentElement.style.setProperty("--fb-hero-top", Math.max(0, Math.round(top)) + "px");
    };
    setHeroTop();
    window.addEventListener("load", setHeroTop);
    window.addEventListener("resize", setHeroTop);
  }

  // ---- "Add a side" carousel ----------------------------------------------
  // The row is a native scroller (swipe on phones); the arrows and dots move
  // it one page of cards at a time on desktop.
  document.querySelectorAll("[data-buffet-carousel]").forEach(function (box) {
    var vp = box.querySelector("[data-carousel-viewport]");
    var prev = box.querySelector("[data-carousel-prev]");
    var next = box.querySelector("[data-carousel-next]");
    var dotsBox = box.querySelector("[data-carousel-dots]");
    if (!vp) return;
    // One page = the visible width plus one gap, so four cards move per page.
    var track = vp.firstElementChild;
    function step() {
      var gap = track ? parseFloat(getComputedStyle(track).columnGap) || 0 : 0;
      return vp.clientWidth + gap;
    }
    function pages() {
      var max = vp.scrollWidth - vp.clientWidth;
      return max <= 2 ? 1 : Math.ceil((max - 2) / step()) + 1;
    }
    function page() {
      return Math.round(vp.scrollLeft / step());
    }
    function go(i) {
      vp.scrollTo({ left: Math.max(0, Math.min(pages() - 1, i)) * step() });
    }
    function paint() {
      var n = pages(), p = Math.min(page(), n - 1);
      if (prev) prev.disabled = p <= 0;
      if (next) next.disabled = p >= n - 1;
      if (!dotsBox) return;
      if (dotsBox.children.length !== n) {
        dotsBox.innerHTML = "";
        for (var i = 0; i < n; i++) {
          var b = document.createElement("button");
          b.type = "button";
          b.setAttribute("aria-label", "Go to slide " + (i + 1));
          b.addEventListener("click", go.bind(null, i));
          dotsBox.appendChild(b);
        }
      }
      Array.prototype.forEach.call(dotsBox.children, function (b, i) {
        b.setAttribute("aria-current", i === p ? "true" : "false");
      });
      dotsBox.hidden = n < 2;
    }
    if (prev) prev.addEventListener("click", function () { go(page() - 1); });
    if (next) next.addEventListener("click", function () { go(page() + 1); });
    var t;
    vp.addEventListener("scroll", function () {
      clearTimeout(t);
      t = setTimeout(paint, 80);
    }, { passive: true });
    window.addEventListener("resize", paint);
    paint();
  });

  // ---- video heading waits for Tolstoy -------------------------------------
  // Tolstoy is held back until marketing consent (tolstoy-consent-fix), which
  // may never come or may come mid-visit. Its heading and empty slot stay
  // hidden until the carousel has actually drawn something.
  var waiting = document.querySelectorAll("[data-buffet-wait-videos]");
  if (waiting.length) {
    var videoSlots = function () {
      return Array.prototype.slice.call(document.querySelectorAll("tolstoy-carousel, tolstoy-stories"));
    };
    var check = function () {
      var ready = videoSlots().some(function (el) {
        return el.children.length > 0 && el.getBoundingClientRect().height > 40;
      });
      // Only the heading is hidden. The slot itself must stay laid out: a
      // widget that renders into a display:none box measures zero width.
      waiting.forEach(function (h) { h.hidden = !ready; });
      return ready;
    };
    if (!check()) {
      var mo = new MutationObserver(function () {
        if (check()) mo.disconnect();
      });
      videoSlots().forEach(function (el) {
        mo.observe(el, { childList: true, subtree: true });
      });
      // Size can arrive after the children do.
      var tries = 0;
      var poll = setInterval(function () {
        if (check() || ++tries > 40) clearInterval(poll);
      }, 1500);
    }
  }

  refresh();
})();
